// Mis pedidos: los últimos primero, con cómo va cada uno. Tocar uno abre el detalle con el seguimiento.
import { formatoGs, formatoFecha } from '../compartido';
import { Icono } from '@reparto/ui';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Boton, ErrorCarga, Vacio } from '../componentes/base';
import { EsqueletoPedidos } from '../componentes/Esqueletos';
import { Pantalla } from '../componentes/Marco';
import { EstadoPedido } from '../componentes/Seguimiento';
import { mensajeDeError } from '../datos/api';
import { useConsulta } from '../datos/consultas';
import { useCuenta, useNegocio } from '../datos/cuenta';
import { fechaEntrega, plural } from '../datos/formato';
import { misPedidos } from '../datos/pedidos';
import type { Pagina, PedidoResumen } from '../datos/tipos';

export function Pedidos() {
  const negocio = useNegocio()!;
  const zona = useCuenta((s) => s.perfil?.empresa.zona_horaria);
  const navegar = useNavigate();
  const c = useConsulta(`pedidos:${negocio.id}`, () => misPedidos(negocio.id));
  const [mas, setMas] = useState<{
    datos: PedidoResumen[];
    siguiente: string | null;
    cargando: boolean;
    error: string | null;
  }>({
    datos: [],
    siguiente: null,
    cargando: false,
    error: null,
  });
  const lista = [...(c.datos?.datos ?? []), ...mas.datos];
  const siguiente = mas.datos.length ? mas.siguiente : (c.datos?.siguiente ?? null);

  async function cargarMas() {
    if (!siguiente) return;
    setMas((m) => ({ ...m, cargando: true, error: null }));
    try {
      const r: Pagina<PedidoResumen> = await misPedidos(negocio.id, siguiente);
      setMas((m) => ({ datos: [...m.datos, ...r.datos], siguiente: r.siguiente, cargando: false, error: null }));
    } catch (e) {
      setMas((m) => ({ ...m, cargando: false, error: mensajeDeError(e) }));
    }
  }

  return (
    <Pantalla titulo="Mis pedidos" subtitulo={negocio.nombre}>
      <div className="px-4 py-4">
        {c.cargando ? (
          <EsqueletoPedidos />
        ) : c.error && !c.datos ? (
          <ErrorCarga mensaje={c.error} sinRed={c.sinRed} onReintentar={() => void c.recargar()} />
        ) : !lista.length ? (
          <Vacio
            icono="receipt_long"
            titulo="Todavía no hiciste pedidos"
            texto="Cuando hagas uno, acá vas a ver cómo va hasta que llega."
          >
            <Boton tamano="lg" icono="storefront" onClick={() => navegar('/')}>
              Hacer un pedido
            </Boton>
          </Vacio>
        ) : (
          <>
            {c.error ? (
              <div className="mb-3">
                <ErrorCarga compacto mensaje={c.error} sinRed={c.sinRed} onReintentar={() => void c.recargar()} />
              </div>
            ) : null}
            <ul className="flex flex-col gap-3">
              {lista.map((p) => (
                <li key={p.id}>
                  <Link
                    to={`/pedidos/${p.id}`}
                    className="block rounded-3xl border border-stone-200 bg-white shadow-sm p-4 transition-colors hover:border-primary-300 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <p className="min-w-0 truncate text-lg leading-tight font-bold text-stone-900">
                        {p.numero_visible}
                      </p>
                      <EstadoPedido s={p.seguimiento} />
                    </div>
                    <p className="text-[15px] text-stone-600">Hecho el {formatoFecha(p.creado_en, zona)}</p>
                    <p className="mt-3 flex items-center gap-1.5 text-[17px] text-stone-800">
                      <Icono nombre="local_shipping" tamano={22} className="text-primary-700" />
                      {p.seguimiento.clave === 'entregado' || p.seguimiento.clave === 'entregado_parcial'
                        ? `Llegó el ${formatoFecha(p.fecha_entrega)}`
                        : p.estado === 'rechazado' || p.estado === 'cancelado'
                          ? `Era para el ${formatoFecha(p.fecha_entrega)}`
                          : `Te llega ${fechaEntrega(p.fecha_entrega, zona)}`}
                    </p>
                    <div className="mt-1 flex items-center justify-between gap-2 text-[15px] text-stone-600">
                      <span>
                        {plural(p.articulos, 'artículo', 'artículos')} · {plural(p.bultos, 'bulto', 'bultos')}
                      </span>
                      <span className="flex items-center gap-1">
                        {p.monto !== null ? (
                          <b className="text-[17px] font-bold text-stone-900 tabular-nums">{formatoGs(p.monto)}</b>
                        ) : null}
                        <Icono nombre="chevron_right" className="text-stone-500" />
                      </span>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
            {mas.error ? (
              <div className="mt-3">
                <ErrorCarga compacto mensaje={mas.error} onReintentar={() => void cargarMas()} />
              </div>
            ) : siguiente ? (
              <Boton
                variante="secundario"
                bloque
                className="mt-3"
                cargando={mas.cargando}
                onClick={() => void cargarMas()}
              >
                Ver pedidos anteriores
              </Boton>
            ) : null}
          </>
        )}
      </div>
    </Pantalla>
  );
}
